import React, { FC, useEffect, useState } from 'react';
import { 
    Dialog, DialogContent, DialogActions, Button, Box, Typography, IconButton,
    FormGroup, FormControlLabel, Checkbox, FormControl, InputLabel, Select, MenuItem, SelectChangeEvent
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import RemoveIcon from '@mui/icons-material/Remove';
import Heading from '../../Components/Heading/heading.tsx';
import { getCompanyRoleFeatures, addRoleFeatures } from './action.ts';
import { toast } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';

interface EditRolesModalProps {
    open: boolean;
    onClose: () => void;
}

const EditRolesModal: FC<EditRolesModalProps> = ({ open, onClose }) => {
    const [selectedRole, setSelectedRole] = useState<string>('');
    const [featuresList, setFeaturesList] = useState<any[]>([]);
    const [selectedFeatures, setSelectedFeatures] = useState<number[]>([]);
    const [featuresFetched, setFeaturesFetched] = useState(false);
    
    const [dashboardExpanded, setDashboardExpanded] = useState<boolean>(true);
    const [reportsExpanded, setReportsExpanded] = useState<boolean>(true);

    useEffect(() => {
        if (open) {
            setSelectedRole('');
            setFeaturesFetched(false);
            setFeaturesList([]);
            setSelectedFeatures([]);
            
            getCompanyRoleFeatures(
                1000,
                0,
                (response: any) => {
                    const fetchedFeatures = response?.message?.features || response?.data?.message?.features || [];
                    setFeaturesList(fetchedFeatures);
                    const preSelectedIds = fetchedFeatures
                        .filter((f: any) => f.checked === true)
                        .map((f: any) => f.id);
                        
                    setSelectedFeatures(preSelectedIds);
                    setFeaturesFetched(true);
                },
                (error: any) => {
                    console.error("Failed to fetch features", error);
                }
            );
        }
    }, [open]);

    const handleRoleChange = (event: SelectChangeEvent<string>) => {
        setSelectedRole(event.target.value);
    };

    const handleFeatureCheckboxChange = (featureId: number) => {
        setSelectedFeatures((prev) => 
            prev.includes(featureId) ? prev.filter(id => id !== featureId) : [...prev, featureId]
        );
    };

    const handleSave = () => {
        if (!selectedRole) {
            toast.error("Please select a role");
            return;
        }

    const userDetailsFromStorage = JSON.parse(localStorage.getItem("userdetails") || "null");
            const userRole = userDetailsFromStorage?.role?.toLowerCase();
        
        console.log("Logged-in user role:", userRole);
        // Determine the actual role string to pass to the backend
        let finalRoleToPass = selectedRole;

        if (selectedRole === 'supervisor') {
            if (userRole === 'vendor_company_admin') {
                finalRoleToPass = 'vendor_supervisor';
            } else if (userRole === 'client_company_admin') {
                finalRoleToPass = 'client_supervisor';
            }
        }

        addRoleFeatures(
            finalRoleToPass,
            selectedFeatures,
            (res: any) => {
                toast.success("Role features updated successfully");
                onClose();
            },
            (err: any) => {
                toast.error("Failed to save features.");
            }
        );
    };

    const dashboardFeatures = featuresList.filter(f => f.feature_name?.toLowerCase().includes('dashboard'));
    const reportFeatures = featuresList.filter(f => f.feature_name?.toLowerCase().includes('report'));
    const otherFeatures = featuresList.filter(
        f => !f.feature_name?.toLowerCase().includes('dashboard') && !f.feature_name?.toLowerCase().includes('report')
    );

    const renderFeatureCheckboxes = (features: any[]) => (
        <FormGroup sx={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '5px' }}>
            {features.map((feature: any) => (
                <FormControlLabel
                    key={feature.id}
                    control={
                        <Checkbox
                            checked={selectedFeatures.includes(feature.id)}
                            onChange={() => handleFeatureCheckboxChange(feature.id)}
                        />
                    }
                    label={
                        feature.feature_name 
                            ? feature.feature_name
                                  .replace(/_/g, ' ')
                                  .replace(/\b\w/g, (char: string) => char.toUpperCase())
                            : `Feature ${feature.id}`
                    } 
                />
            ))}
        </FormGroup>
    );

    return (
        <Dialog open={open} onClose={onClose} fullWidth maxWidth="sm">
            <Box sx={{ marginLeft: { xs: '0px', sm: '-180px' } }}>
                <Heading title="Edit Roles" closeIcon="true" onClose={onClose} />
            </Box>
            <DialogContent style={{ paddingBottom: '10px' }}>
                <Box sx={{ marginTop: '10px', marginBottom: '20px' }}>
                    <FormControl fullWidth size="small">
                        <InputLabel>Select Role</InputLabel>
                        <Select
                            value={selectedRole}
                            label="Select Role"
                            onChange={handleRoleChange}
                        >
                            <MenuItem value="driver">Driver</MenuItem>
                            <MenuItem value="supervisor">Supervisor</MenuItem>
                        </Select>
                    </FormControl>
                </Box>

                <Box sx={{ mt: 2 }}>
                    {featuresList.length === 0 && featuresFetched ? (
                        <Typography color="textSecondary">No features available.</Typography>
                    ) : (
                        <>
                            {dashboardFeatures.length > 0 && (
                                <Box sx={{ mb: 2 }}>
                                    <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                                        <Typography sx={{ fontWeight: 'bold', fontSize: '14px' }}>
                                            Dashboard
                                        </Typography>
                                        <IconButton size="small" onClick={() => setDashboardExpanded(!dashboardExpanded)} sx={{ padding: '4px' }}>
                                            {dashboardExpanded ? <RemoveIcon /> : <AddIcon />}
                                        </IconButton>
                                    </Box>
                                    {dashboardExpanded && (
                                        <Box sx={{ paddingLeft: '15px', mt: 1.5 }}>
                                            {renderFeatureCheckboxes(dashboardFeatures)}
                                        </Box>
                                    )}
                                </Box>
                            )}

                            {reportFeatures.length > 0 && (
                                <Box sx={{ mb: 2 }}>
                                    <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                                        <Typography sx={{ fontWeight: 'bold', fontSize: '14px' }}>
                                            Reports
                                        </Typography>
                                        <IconButton size="small" onClick={() => setReportsExpanded(!reportsExpanded)} sx={{ padding: '4px' }}>
                                            {reportsExpanded ? <RemoveIcon /> : <AddIcon />}
                                        </IconButton>
                                    </Box>
                                    {reportsExpanded && (
                                        <Box sx={{ paddingLeft: '15px', mt: 1.5 }}>
                                            {renderFeatureCheckboxes(reportFeatures)}
                                        </Box>
                                    )}
                                </Box>
                            )}

                            {otherFeatures.length > 0 && (
                                <Box sx={{ mb: 0 }}>
                                    <Typography sx={{ fontWeight: 'bold', fontSize: '14px', mb: 1.5 }}>
                                        General Features
                                    </Typography>
                                    <Box sx={{ paddingLeft: '15px' }}>
                                        {renderFeatureCheckboxes(otherFeatures)}
                                    </Box>
                                </Box>
                            )}
                        </>
                    )}
                </Box>
            </DialogContent>
            <DialogActions style={{ justifyContent: 'center', paddingBottom: '20px' }}>
                <Button onClick={onClose} className="custom-cancel-button" style={{ marginRight: '10px' }}>
                    Cancel
                </Button>
                <Button onClick={handleSave} className="custom-button" style={{ marginLeft: '10px' }}>
                    Save
                </Button>
            </DialogActions>
        </Dialog>
    );
};

export default EditRolesModal;